/**
 * StatusChip molecule — a leading-icon status chip (e.g. "Starting XI").
 *
 * Atoms only. Colors come from the Badge atom's `TONE_CLASS["success-soft"]`
 * pairing — the v7 design binds `success/success0` bg + `success/success900`
 * text (dark: rgb(27,50,36) / rgb(202,255,232)); the earlier `status-success-surface`/
 * `status-success` approximation resolved to (28,43,33)/(72,151,102)
 * (SE-5522 pixel audit). Defaults to a check icon; the caller can pass any
 * lucide icon via `icon`.
 * @module components/molecules/StatusChip/StatusChipView
 */
import { Check } from "lucide-react-native";
import React, { memo } from "react";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

const TONE = TONE_CLASS["success-soft"];

/** Props for StatusChipView */
interface StatusChipViewProps {
  /** Chip label */
  readonly label: string;
  /** Leading lucide icon (defaults to a check) */
  readonly icon?: React.ElementType;
  /** Optional testID */
  readonly testID?: string;
}

/**
 * Renders a status chip with a leading icon.
 * @param root0 - Component props
 * @param root0.label - Chip label
 * @param root0.icon - Leading lucide icon (defaults to a check)
 * @param root0.testID - Optional testID
 */
const StatusChipView = ({
  label,
  icon = Check,
  testID,
}: StatusChipViewProps) => (
  <Stack
    testID={testID}
    direction="horizontal"
    className={`items-center gap-1 rounded-full px-2 py-1 ${TONE.box}`}
  >
    <Icon as={icon} size="2xs" className={TONE.text} />
    <Text variant="micro" className={TONE.text}>
      {label}
    </Text>
  </Stack>
);

StatusChipView.displayName = "StatusChipView";

export default memo(StatusChipView);
